import type {QueryFunctionContext, UseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, useQuery} from '@tanstack/react-query'

import type {SupportedLocales} from '@/lib/locale/locale'
import type {MultiFullProduct} from '@/models/productModel'
import type {ProductId} from '@/routes/_shop/product/$productId'
import {getProductDetails} from '@/services/productService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getProductDetailsQueryKey(
  params: ProductId & {locale: SupportedLocales; minimal?: boolean},
) {
  return ['productDetails', params.retailer, params.id, params.locale, !!params.minimal] as const
}

export function getProductDetailsQueryOptions<TData = MultiFullProduct | false, TError = Error>(
  params: ProductId & {locale: SupportedLocales; minimal?: boolean; preload?: boolean},
  options?: Omit<UseQueryOptions<MultiFullProduct | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  const {minimal = false, preload = false, ...rest} = params
  return queryOptions({
    queryKey: getProductDetailsQueryKey({...rest, minimal}),
    queryFn: async (ctx: QueryFunctionContext) => {
      const [, retailer, id, locale, minimalFromKey] = ctx.queryKey as readonly [
        'productDetails',
        string,
        string,
        SupportedLocales,
        boolean,
      ]
      const preloadFromMeta = (ctx.meta as {preload?: boolean} | undefined)?.preload ?? false
      return getProductDetails({
        retailer,
        id,
        locale,
        minimal: minimalFromKey,
        preload: preloadFromMeta,
      })
    },
    placeholderData: keepPreviousData,
    staleTime: 15 * 60 * 100, // keep alignment with previous behavior
    ...NO_REFETCH_OPTIONS,
    meta: {preload},
    ...options,
  })
}

export default function useProductDetails<TData = MultiFullProduct | false, TError = Error>(
  params: ProductId & {locale: SupportedLocales; minimal?: boolean; preload?: boolean},
  options?: Omit<UseQueryOptions<MultiFullProduct | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return useQuery(getProductDetailsQueryOptions<TData, TError>(params, options))
}
